import { useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Form } from "react-bootstrap";
import { Toast } from "primereact/toast";
import SelectField from "components/Ui/SelectField/SelectField";
import useCity from "hooks/useCity";
import {
    useAssignDriverShift,
    useCreateShiftTemplate,
    useDriverShiftWeek,
    useSetShiftSeats,
    useUpdateCityDriverShifts,
    useUpdateDriverShift,
    useUpdateShiftTemplate,
} from "hooks/useDriverShifts";
import { addDays, palestineToday, seatState } from "utils/driverShiftWeek";
import { formatDate, formatDateTime, formatTime, formatWeekday } from "utils/dateFormat";
import ShiftTemplateModal, { shiftFormFrom } from "./ShiftTemplateModal";
import "components/Ui/AppPresenceBadge/appPresenceBadge.scss";
import "./driverShifts.scss";

/**
 * «الخميس 01/10/2026, 8:00 PM» inside an Arabic sentence. The date and the time
 * are left-to-right text, so they sit in their own `<bdi>`: without it the
 * browser moved «PM» and the comma to the wrong side.
 *
 * @param {{value: string}} props - "YYYY-MM-DD" or "YYYY-MM-DD HH:mm", Palestine time
 */
const WeekdayMoment = ({ value }) => (
    <>
        {formatWeekday(value)} <bdi dir="ltr">{formatDateTime(value)}</bdi>
    </>
);

/** A driver is in a cell only with one of these; the other statuses are history. */
const ACTIVE = ["booked", "assigned"];

const errorText = (error) => error?.response?.data?.message || "حدث خطأ غير متوقع";

/**
 * The seats of one cell, and the form that changes them for that date only.
 */
const CellSeats = ({ cell, onSave }) => {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState("");
    const { label, tone } = seatState(cell);

    if (editing) {
        return (
            <form
                className="driver-shifts__seats-form"
                onSubmit={async (event) => {
                    event.preventDefault();
                    await onSave(value.trim());
                    setEditing(false);
                }}
            >
                <Form.Control
                    size="sm"
                    type="number"
                    min={0}
                    max={500}
                    inputMode="numeric"
                    value={value}
                    placeholder="افتراضي"
                    aria-label="عدد الأماكن لهذا اليوم"
                    autoFocus
                    onChange={(event) => setValue(event.target.value)}
                />
                <button type="submit" className="driver-shifts__link">
                    حفظ
                </button>
                <button
                    type="button"
                    className="driver-shifts__link"
                    onClick={() => setEditing(false)}
                >
                    إلغاء
                </button>
            </form>
        );
    }

    return (
        <button
            type="button"
            className={`driver-shifts__seats driver-shifts__seats--${tone}`}
            title="تغيير عدد الأماكن لهذا اليوم فقط"
            onClick={() => {
                setValue(cell.seatsOverridden ? String(cell.seats) : "");
                setEditing(true);
            }}
        >
            <bdi dir="ltr">{label}</bdi>
            {cell.seatsOverridden ? <span className="driver-shifts__seats-mark">*</span> : null}
        </button>
    );
};

/**
 * One driver in a cell. A shift the driver booked himself (phase 2) has a
 * dashed edge and «رفض»; a green dot says he is on shift now.
 */
const DriverChip = ({ row, onShiftNow, onReject, onRemove }) => {
    const booked = row.status === "booked";
    const name = row.driver.name || `#${row.driver.id}`;
    return (
        <li
            className={`driver-shifts__driver${booked ? " driver-shifts__driver--booked" : ""}`}
            title={booked ? "حجزها السائق بنفسه" : "أضافها المشرف"}
        >
            {onShiftNow ? <span className="driver-shifts__now" title="في الوردية الآن" /> : null}
            <bdi>{name}</bdi>
            {booked ? (
                <button
                    type="button"
                    className="driver-shifts__link"
                    aria-label={`رفض حجز ${name}`}
                    onClick={onReject}
                >
                    رفض
                </button>
            ) : null}
            <button
                type="button"
                className="driver-shifts__remove"
                aria-label={`إزالة ${name} من الوردية`}
                onClick={onRemove}
            >
                ×
            </button>
        </li>
    );
};

/**
 * ورديات السائقين (admin#74, api#198): the week grid of one city.
 *
 * THE GRID SHOWS ONE SHIFT WEEK of the city (Saturday to Friday by default),
 * not seven days from today: bookings, seats and the weekly limit all count by
 * that week, so a rolling window split one week across two screens. Without
 * `start` in the URL, the API opens the current week. Today is marked, and
 * the days before it are dimmed.
 *
 * Rows are the city's shifts. Each cell shows its seats (used/total) and its
 * drivers, and adds or removes a driver. An admin may go above the seats; the
 * page says so.
 *
 * The rules that these shifts drive (who gets the alarm, who can take an
 * order, the overflow, the booking) are on the server. This page only edits
 * the rows.
 */
const DriverShifts = () => {
    const toast = useRef(null);
    const { cities, fetchCities } = useCity();
    const adminInfo = useSelector((state) => state.auth.userData);
    const isSuperAdmin = adminInfo?.super_admin === true;
    const visibleCities = useMemo(
        () =>
            isSuperAdmin
                ? (cities ?? [])
                : (cities ?? []).filter((city) => String(city.id) === String(adminInfo?.cityId)),
        [adminInfo?.cityId, cities, isSuperAdmin],
    );

    // The city and the week live in the URL, so a reload or a shared link
    // opens the same grid. No `start` means the city's current week.
    const [searchParams, setSearchParams] = useSearchParams();
    const cityId = searchParams.get("cityId") || "";
    const requestedStart = searchParams.get("start") || "";
    const start = /^\d{4}-\d{2}-\d{2}$/.test(requestedStart) ? requestedStart : "";
    const setParam = (key, value) => {
        const next = new URLSearchParams(searchParams);
        if (value) next.set(key, value);
        else next.delete(key);
        setSearchParams(next, { replace: true });
    };
    const [editing, setEditing] = useState(null);
    const [shiftForm, setShiftForm] = useState(() => shiftFormFrom(null));
    const openEditor = (template) => {
        setShiftForm(shiftFormFrom(template));
        setEditing(template ?? {});
    };

    const effectiveCityId =
        cityId || (!isSuperAdmin && visibleCities[0] ? String(visibleCities[0].id) : "");
    const city = visibleCities.find((row) => String(row.id) === String(effectiveCityId)) || null;
    const { week, loading, error } = useDriverShiftWeek(effectiveCityId, start);

    const createTemplate = useCreateShiftTemplate();
    const updateTemplate = useUpdateShiftTemplate();
    const setSeats = useSetShiftSeats();
    const assign = useAssignDriverShift();
    const updateShift = useUpdateDriverShift();
    const updateSettings = useUpdateCityDriverShifts();
    // The city whose «تفعيل» waits for a second click. Held as a city id, so a
    // change of city drops it with no effect.
    const [confirmingOnFor, setConfirmingOnFor] = useState("");

    const show = (severity, summary, detail) =>
        toast.current?.show({ severity, summary, detail, life: 3500 });

    const run = async (action, success) => {
        try {
            const result = await action();
            if (success) show("success", "تم الحفظ", success);
            return result;
        } catch (err) {
            show("error", "تعذر الحفظ", errorText(err));
            return null;
        }
    };

    const saveTemplate = async (values) => {
        const saved = await run(
            () =>
                editing?.id
                    ? updateTemplate.mutateAsync({ id: editing.id, ...values })
                    : createTemplate.mutateAsync({ cityId: Number(effectiveCityId), ...values }),
            editing?.id ? "تم تعديل الوردية" : "تمت إضافة الوردية",
        );
        if (saved) setEditing(null);
    };

    const addDriver = async (templateId, date, driverUserId) => {
        const result = await run(() =>
            assign.mutateAsync({ templateId, date, driverUserId: Number(driverUserId) }),
        );
        if (!result) return;
        if (result.overSeats) {
            show(
                "warn",
                "فوق عدد الأماكن",
                `في هذه الوردية ${result.used} سائق والأماكن ${result.seats}.`,
            );
        } else {
            show("success", "تمت الإضافة", "أضيف السائق إلى الوردية");
        }
    };

    // THE SWITCH IS ON THIS PAGE, not only in the city settings: the page shows
    // the state, so it must be able to change it. Off acts at once (the safe
    // direction). On, with nobody on shift in the next 7 days, needs a second
    // click: every driver would become a reserve driver.
    const setShiftsEnabled = async (enabled) => {
        if (enabled && settings?.upcomingAssignments === 0 && confirmingOnFor !== effectiveCityId) {
            setConfirmingOnFor(effectiveCityId);
            return;
        }
        setConfirmingOnFor("");
        const saved = await run(
            () => updateSettings.mutateAsync({ cityId: Number(effectiveCityId), enabled }),
            enabled ? "تم تفعيل الورديات في هذه المدينة" : "تم إيقاف الورديات في هذه المدينة",
        );
        if (saved) fetchCities();
    };

    const setDriverShift = (shiftId, status, message) =>
        run(() => updateShift.mutateAsync({ id: shiftId, status }), message);

    const cityOptions = visibleCities.map((row) => ({ value: String(row.id), label: row.name }));
    const drivers = week?.drivers ?? [];
    const onShiftNow = new Set(week?.onShiftNow ?? []);
    // THE STATE COMES FROM THE SERVER, read with the week. The cached city list
    // can be older than the switch.
    const settings = week?.settings ?? null;
    const booking = settings?.shiftsEnabled ? (week?.booking ?? null) : null;
    const today = week?.today ?? palestineToday();
    const isCurrentWeek = week ? week.start === week.currentWeekStart : true;
    const isBookingWeek = Boolean(booking && week && week.start === booking.weekStart);
    const weekTitle = isCurrentWeek ? "هذا الأسبوع" : isBookingWeek ? "أسبوع الحجز" : "الأسبوع";

    return (
        <div className="driver-shifts">
            <Toast style={{ direction: "rtl" }} ref={toast} position="top-left" />

            <section className="surface-card driver-shifts__card">
                <div className="driver-shifts__heading">
                    <h3>
                        ورديات السائقين
                        {!isSuperAdmin && city ? (
                            <span className="driver-shifts__city-name">
                                {" "}
                                — <bdi>{city.name}</bdi>
                            </span>
                        ) : null}
                    </h3>
                    <div className="driver-shifts__toolbar">
                        {isSuperAdmin ? (
                            <div className="driver-shifts__city">
                                <SelectField
                                    value={
                                        cityOptions.find(
                                            (option) => option.value === effectiveCityId,
                                        ) || null
                                    }
                                    options={cityOptions}
                                    placeholder="اختر المدينة"
                                    onChange={(option) => setParam("cityId", option?.value ?? "")}
                                />
                            </div>
                        ) : null}
                        <button
                            type="button"
                            className="button driver-shifts__add"
                            disabled={!effectiveCityId}
                            onClick={() => openEditor(null)}
                        >
                            إضافة وردية
                        </button>
                    </div>
                </div>

                {settings ? (
                    <div className="driver-shifts__summary">
                        <div className="driver-shifts__pills">
                            <span
                                className={`app-presence app-presence--${settings.shiftsEnabled ? "on" : "off"}`}
                            >
                                <span className="app-presence__dot" />
                                {settings.shiftsEnabled ? "الورديات مفعّلة" : "الورديات غير مفعّلة"}
                            </span>
                            <button
                                type="button"
                                className={`btn btn-sm ${settings.shiftsEnabled ? "btn-outline-secondary" : "btn-outline-success"} driver-shifts__switch`}
                                disabled={updateSettings.isPending}
                                onClick={() => setShiftsEnabled(!settings.shiftsEnabled)}
                            >
                                {settings.shiftsEnabled ? "إيقاف الورديات" : "تفعيل الورديات"}
                            </button>
                            {booking ? (
                                <span
                                    className={`app-presence app-presence--${booking.open ? "on" : "idle"}`}
                                >
                                    <span className="app-presence__dot" />
                                    {booking.open ? "الحجز مفتوح" : "الحجز مغلق"}
                                </span>
                            ) : null}
                            <span className="driver-shifts__metric">
                                <bdi dir="ltr">{onShiftNow.size}</bdi> في الوردية الآن
                            </span>
                            <span className="driver-shifts__metric">
                                <bdi dir="ltr">{week.driversWithoutShift.length}</bdi> بدون وردية
                                هذا الأسبوع
                            </span>
                        </div>
                        {confirmingOnFor === effectiveCityId && !settings.shiftsEnabled ? (
                            <div className="form-warning driver-shifts__confirm">
                                <span>
                                    لا يوجد أي سائق في وردية خلال الأيام السبعة القادمة. بعد التفعيل
                                    يصبح كل السائقين احتياطًا، ولا يصلهم الطلب إلا بعد{" "}
                                    <bdi dir="ltr">{settings.overflowMinutes}</bdi> دقائق.
                                </span>
                                <span className="driver-shifts__confirm-actions">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-warning"
                                        onClick={() => setShiftsEnabled(true)}
                                    >
                                        فعّلها على أي حال
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => setConfirmingOnFor("")}
                                    >
                                        إلغاء
                                    </button>
                                </span>
                            </div>
                        ) : null}
                        <div className="driver-shifts__hint">
                            {settings.shiftsEnabled
                                ? `بعد ${settings.overflowMinutes} دقائق بدون سائق، يصل الطلب لسائقين خارج ورديتهم وهم متصلون.`
                                : "قبل التفعيل يعمل كل السائقين كالمعتاد: يصلهم كل طلب جديد في مناطقهم."}
                            {booking ? (
                                <>
                                    {" "}
                                    الحجز لأسبوع <WeekdayMoment value={booking.weekStart} />: يفتح{" "}
                                    <WeekdayMoment value={booking.opensAt} /> ويغلق{" "}
                                    <WeekdayMoment value={booking.closesAt} />
                                    {settings.maxShiftsPerDriver > 0
                                        ? `، حتى ${settings.maxShiftsPerDriver} ورديات لكل سائق`
                                        : ""}
                                    .
                                </>
                            ) : null}
                        </div>
                    </div>
                ) : null}

                {week ? (
                    <div className="driver-shifts__weekbar">
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm driver-shifts__arrow"
                            aria-label="الأسبوع السابق"
                            onClick={() => setParam("start", addDays(week.start, -7))}
                        >
                            →
                        </button>
                        <div className="driver-shifts__weeklabel">
                            <strong>{weekTitle}</strong>
                            <span>
                                من {formatWeekday(week.dates[0])}{" "}
                                <bdi dir="ltr">{formatDate(week.dates[0])}</bdi> إلى{" "}
                                {formatWeekday(week.dates[6])}{" "}
                                <bdi dir="ltr">{formatDate(week.dates[6])}</bdi>
                            </span>
                        </div>
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm driver-shifts__arrow"
                            aria-label="الأسبوع التالي"
                            onClick={() => setParam("start", addDays(week.start, 7))}
                        >
                            ←
                        </button>
                        <div className="driver-shifts__jumps">
                            {!isCurrentWeek ? (
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={() => setParam("start", "")}
                                >
                                    هذا الأسبوع
                                </button>
                            ) : null}
                            {booking && !isBookingWeek ? (
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={() => setParam("start", booking.weekStart)}
                                >
                                    أسبوع الحجز
                                </button>
                            ) : null}
                        </div>
                    </div>
                ) : null}

                {!effectiveCityId ? (
                    <div className="driver-shifts__empty">اختر المدينة لعرض ورديات الأسبوع.</div>
                ) : loading ? (
                    <div className="driver-shifts__empty">جاري التحميل…</div>
                ) : error ? (
                    <div className="driver-shifts__empty">{errorText(error)}</div>
                ) : !week?.templates?.length ? (
                    <div className="driver-shifts__empty">
                        لا توجد ورديات لهذه المدينة بعد. ابدأ بـ «إضافة وردية».
                    </div>
                ) : (
                    <div className="driver-shifts__scroll">
                        <table className="driver-shifts__grid">
                            <thead>
                                <tr>
                                    <th className="driver-shifts__corner">الوردية</th>
                                    {week.dates.map((date) => (
                                        <th
                                            key={date}
                                            className={
                                                date === today
                                                    ? "driver-shifts__today"
                                                    : date < today
                                                      ? "driver-shifts__past"
                                                      : undefined
                                            }
                                        >
                                            <span>{formatWeekday(date)}</span>
                                            <bdi dir="ltr">{formatDate(date)}</bdi>
                                            {date === today ? (
                                                <em className="driver-shifts__today-tag">اليوم</em>
                                            ) : null}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {week.templates.map((template) => (
                                    <tr
                                        key={template.id}
                                        className={
                                            template.active ? undefined : "driver-shifts__retired"
                                        }
                                    >
                                        <th scope="row" className="driver-shifts__shift">
                                            <div className="driver-shifts__shift-name">
                                                <strong>
                                                    <bdi>{template.name}</bdi>
                                                </strong>
                                                <button
                                                    type="button"
                                                    className="driver-shifts__link"
                                                    onClick={() => openEditor(template)}
                                                >
                                                    تعديل
                                                </button>
                                            </div>
                                            <span className="driver-shifts__time">
                                                <bdi dir="ltr">
                                                    {formatTime(template.start_time)} →{" "}
                                                    {formatTime(template.end_time)}
                                                </bdi>
                                            </span>
                                            {template.crossesMidnight ? (
                                                <span className="driver-shifts__hint">
                                                    حتى اليوم التالي
                                                </span>
                                            ) : null}
                                            {template.active ? null : (
                                                <span className="app-presence app-presence--off">
                                                    موقوفة
                                                </span>
                                            )}
                                        </th>
                                        {template.cells.map((cell) => {
                                            const inCell = cell.drivers.filter((row) =>
                                                ACTIVE.includes(row.status),
                                            );
                                            const taken = new Set(
                                                inCell.map((row) => row.driver.id),
                                            );
                                            return (
                                                <td
                                                    key={cell.date}
                                                    className={`driver-shifts__cell${cell.date < today ? " driver-shifts__past" : ""}`}
                                                >
                                                    <CellSeats
                                                        cell={cell}
                                                        onSave={(value) =>
                                                            run(
                                                                () =>
                                                                    setSeats.mutateAsync({
                                                                        templateId: template.id,
                                                                        date: cell.date,
                                                                        seats:
                                                                            value === ""
                                                                                ? null
                                                                                : Number(value),
                                                                    }),
                                                                "تم تغيير عدد الأماكن لهذا اليوم",
                                                            )
                                                        }
                                                    />
                                                    {inCell.length ? (
                                                        <ul className="driver-shifts__drivers">
                                                            {inCell.map((row) => (
                                                                <DriverChip
                                                                    key={row.shiftId}
                                                                    row={row}
                                                                    onShiftNow={onShiftNow.has(
                                                                        row.driver.id,
                                                                    )}
                                                                    onReject={() =>
                                                                        setDriverShift(
                                                                            row.shiftId,
                                                                            "rejected",
                                                                            "رُفض الحجز. لا يستطيع السائق حجز هذه الوردية مرة أخرى",
                                                                        )
                                                                    }
                                                                    onRemove={() =>
                                                                        setDriverShift(
                                                                            row.shiftId,
                                                                            "cancelled",
                                                                            "أزيل السائق من الوردية",
                                                                        )
                                                                    }
                                                                />
                                                            ))}
                                                        </ul>
                                                    ) : null}
                                                    {template.active ? (
                                                        <Form.Select
                                                            size="sm"
                                                            className="driver-shifts__add-driver"
                                                            value=""
                                                            aria-label="إضافة سائق"
                                                            onChange={(event) =>
                                                                event.target.value &&
                                                                addDriver(
                                                                    template.id,
                                                                    cell.date,
                                                                    event.target.value,
                                                                )
                                                            }
                                                        >
                                                            <option value="">+ سائق</option>
                                                            {drivers
                                                                .filter(
                                                                    (driver) =>
                                                                        !taken.has(driver.id),
                                                                )
                                                                .map((driver) => (
                                                                    <option
                                                                        key={driver.id}
                                                                        value={driver.id}
                                                                    >
                                                                        {driver.name ||
                                                                            `#${driver.id}`}
                                                                    </option>
                                                                ))}
                                                        </Form.Select>
                                                    ) : null}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {week?.templates?.length ? (
                    <ul className="driver-shifts__legend">
                        <li>
                            <span className="driver-shifts__seats driver-shifts__seats--open">
                                <bdi dir="ltr">1/2</bdi>
                            </span>
                            الأماكن المشغولة من العدد. اضغط لتغييره ليوم واحد، و* يوم بعدد خاص
                        </li>
                        <li>
                            <span className="driver-shifts__driver driver-shifts__driver--booked driver-shifts__sample">
                                سائق
                            </span>
                            حجزها السائق بنفسه، و«رفض» يمنعه من حجزها مرة أخرى
                        </li>
                        <li>
                            <span className="driver-shifts__now" />
                            في الوردية الآن
                        </li>
                    </ul>
                ) : null}
            </section>

            {week ? (
                <section className="surface-card driver-shifts__card">
                    <div className="driver-shifts__heading">
                        <h3>سائقين بدون ورديات هذا الأسبوع</h3>
                        <span className="driver-shifts__hint">
                            <bdi dir="ltr">{week.driversWithoutShift.length}</bdi> من{" "}
                            <bdi dir="ltr">{drivers.length}</bdi>
                        </span>
                    </div>
                    {week.driversWithoutShift.length ? (
                        <ul className="driver-shifts__spare">
                            {week.driversWithoutShift.map((driver) => (
                                <li key={driver.id}>
                                    <span
                                        className={`app-presence app-presence--${driver.online ? "on" : "off"}`}
                                    >
                                        <span className="app-presence__dot" />
                                        <bdi>{driver.name || `#${driver.id}`}</bdi>
                                    </span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className="driver-shifts__empty">
                            كل السائقين لديهم وردية في هذا الأسبوع.
                        </div>
                    )}
                    <div className="driver-shifts__hint driver-shifts__footnote">
                        السائق بدون وردية يعمل احتياطيًا: يصله الطلب فقط بعد أن يمر وقت الانتظار
                        بدون سائق، وفقط إذا كان متصلًا.
                    </div>
                </section>
            ) : null}

            <ShiftTemplateModal
                show={editing !== null}
                close={() => setEditing(null)}
                city={city}
                template={editing?.id ? editing : null}
                form={shiftForm}
                setForm={setShiftForm}
                saving={createTemplate.isPending || updateTemplate.isPending}
                onSave={saveTemplate}
            />
        </div>
    );
};

export default DriverShifts;
